import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { Location } from '@angular/common';
import { Subscription, Subject, EMPTY, distinctUntilChanged } from 'rxjs';
import { map, concatMap, debounceTime, filter, switchMap, takeUntil, catchError } from 'rxjs/operators';
import { TreeNode } from 'primeng/api';
import { MessageService } from 'primeng/api';
import { Router, ActivatedRoute } from '@angular/router';

import { Document, DocumentFastSearchResult } from 'src/app/shared/document';
import { DocListEntry, DocTypes } from '../../shared/doc-list-entry';
import { RestApiService } from '../../services/rest-api.service';
import { DocumentApiService } from '../../services/document-api.service';

/**
 * Reusable document selector that resolves and displays a linked/parent document by ID.
 *
 * Selection can be initiated in three ways:
 * 1. **Tree dialog** — `onDocumentTreeButton` opens `app-documents-tree`; the user navigates
 *    the hierarchy and picks a node, which calls `loadDocument(docId, emit=true)`.
 * 2. **Search dialog** — `onDocumentSearchButton` opens `app-documents-search`; the user
 *    performs a text search and picks a result, which calls `loadDocument(docId, emit=true)`.
 * 3. **Live type-ahead** — typing into the `p-autoComplete` input drives `doSearch()` →
 *    debounced `searchStr$` → `switchMap(getDocumentFastSearch())`, same pattern as
 *    `ProductselectorComponent`. Backed by `GET /DocumentList/fastSearchByNumber`
 *    (`WY_tvf_searchDocumentByNumber_iTVF`) rather than the search dialog's
 *    `getDocListDateEx`, which can take up to 45 seconds per that dialog's own warning
 *    and is unusable for live typing. Also NOT the original `WY_tvf_searchDocumentFast`
 *    this used at first (2026-09-16) — real live evidence showed that one genuinely
 *    slow/unreliable (up to 13.4s, outright 30s timeouts); a new, narrow document-
 *    number-only function replaced it, see `document-api.service.ts`'s
 *    `getDocumentFastSearch()` and `WORKLOG.md`. Picking a suggestion calls
 *    `loadDocument(docId, emit=true)` the same as the other two paths — the search
 *    endpoint returns a reduced column set, not a full `Document`, so the full
 *    `Document`/display name is still resolved through the existing pipeline.
 *
 * When `documentId` changes via `@Input`, the component silently resolves the document without
 * emitting (`loadDocument(id, emit=false)`).
 *
 * The internal load pipeline uses a `loadDoc$` Subject → sequential `concatMap` of two API calls
 * (`getDocumentById` → `getDocumentNameById`) so the display string is always consistent with the
 * resolved model. `distinctUntilChanged` prevents redundant re-fetches for the same ID.
 *
 * **Output:** `selectedDocument` emits the full `Document` on user-initiated selection,
 * or `null` when the user resets the field.
 */
@Component({
  selector: 'app-documentselector',
  templateUrl: './documentselector.component.html',
  styleUrls: ['./documentselector.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class DocumentselectorComponent implements OnInit, OnDestroy, OnChanges {

  docTreeNodes: TreeNode<Document>[] = [{}];
  currentNode: TreeNode<Document>;

  topmostDoc: Document;
  childDocs: Document[];
  selectedDoc: Document;

  selDocQuery: Subscription;
  topDocQuery: Subscription;
  childDocQuery: Subscription;

  displayDC: boolean = false;
  displayDS: boolean = false;

  selectedDocStr: string = '';
  selectedDocId: number = 0;

  /** Live type-ahead suggestions, populated by the `searchStr$` → `getDocumentFastSearch` pipeline. */
  docSearchResults: DocumentFastSearchResult[] = [];

  /** `true` if the currently selected document has a parent (`PRIMDOCID` is non-null). */
  public get isThereParent(): boolean {
    //console.log('*** DocumentselectorComponent disableParentBtn: ===> return value: ' + !((this.selectedDoc != null) && (this.selectedDoc.PRIMDOCID != null)));
    return ((this.selectedDoc != null) && (this.selectedDoc.PRIMDOCID != null));
  }

  /** `true` if a document is currently selected. */
  public get isThereSelection(): boolean {
    //console.log('*** DocumentselectorComponent disableParentBtn: ===> return value: ' + !((this.selectedDoc != null) && (this.selectedDoc.PRIMDOCID != null)));
    return ((this.selectedDoc != null));
  }

  /** Label text displayed above the selector field. */
  @Input() labelKey: string;

  /** When `false`, the label above the field is hidden. */
  @Input() showLabel: boolean = true;

  /** Pre-selection seed: a change here silently resolves and displays the document without emitting. */
  @Input() documentId: number;

  /** When `true`, the selector is rendered as read-only. */
  @Input() disabled: boolean = false;

  /** Placeholder text shown when no document is selected. */
  @Input() placeholder: string = 'Выбрать первичный документ...';

  /** Optional live type-ahead narrowing (PR #80): `DOCCLIENTSID` filter — `null` (default)
   *  means no filter, identical search scope to before these inputs existed. */
  @Input() filterClientId: number = null;

  /** Optional live type-ahead narrowing: `DOCLCOCLIID` filter (the local/legal entity
   *  issuing the document) — `null` (default) means no filter. */
  @Input() filterLcoClientId: number = null;

  /** Optional live type-ahead narrowing: `DOCTYPE` filter (e.g. `1` to restrict to
   *  Invoices) — `null` (default) means no filter. */
  @Input() filterDocType: number = null;

  /** Emits the selected `Document` on user-initiated selection, or `null` on reset. */
  @Output() public selectedDocument: EventEmitter<Document> = new EventEmitter();

  /** Internal Subject driving the `concatMap` load pipeline. Carries `docid` and `emit` flag. */
  loadDoc$ = new Subject<{ docid: number; emit: boolean; }>();

  /** Internal Subject driving the debounced `getDocumentFastSearch` type-ahead pipeline. */
  private searchStr$ = new Subject<string>();

  /** Tears down the type-ahead pipeline on destroy. */
  private readonly destroy$ = new Subject<void>();

  constructor(
    private restapi: RestApiService,
    private docApi: DocumentApiService,
    private messageService: MessageService,
    private router: Router,
    private location: Location
) { }

  /**
   * Pushes a load request onto `loadDoc$`.
   * If `doc` is falsy, immediately clears the selection without an API call.
   * @param doc - Document ID to load, or `null`/`0` to clear.
   * @param emit - When `true`, `selectedDocument` is emitted after the load completes.
   */
  loadDocument(doc: number, emit: boolean): void {
    if (doc) {
      this.loadDoc$.next({ docid: doc, emit: emit });
    } else {
      this.selectedDoc = null;
      this.selectedDocStr = '';
      this.selectedDocId = null;
    }
  }

  /** Reacts to `documentId` input changes by silently loading the new document without emitting. */
  ngOnChanges(changes: SimpleChanges) {
    for (let propName in changes) {
      let change = changes[propName];
      console.log('*** DocumentselectorComponent ngOnChanges (actual): ' + propName + ' ' + JSON.stringify(change.currentValue));

      switch (propName) {
        case 'documentId':
          this.loadDocument(change.currentValue, false);
          break;
      }
    }
  }

  /** Unsubscribes all active HTTP subscriptions to prevent memory leaks. */
  ngOnDestroy(): void {
    if (this.selDocQuery) { this.selDocQuery.unsubscribe(); }

    if (this.topDocQuery) { this.topDocQuery.unsubscribe(); }

    if (this.childDocQuery) { this.childDocQuery.unsubscribe(); }

    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Sets up the `loadDoc$` → `concatMap` load pipeline and triggers an initial load
   * if `documentId` is already populated on component creation.
   */
  ngOnInit(): void {

    this.selDocQuery =
      this.loadDoc$
      .pipe(
        distinctUntilChanged(),

        concatMap(opt => this.docApi.getDocumentById(opt.docid).pipe(map(data => {
          this.selectedDoc = data[0];
          return opt;
        }))),

        concatMap(opt => this.docApi.getDocumentNameById(opt.docid).pipe(map(data => {
          if (this.selectedDoc) {
            this.selectedDoc.DOCNAMESTR = ((data[0] || { DOCNAMESTR: 'Unknown document' }).DOCNAMESTR);
            this.selectedDocStr = this.selectedDoc.DOCNAMESTR;
            this.selectedDocId = this.selectedDoc.DOCID;

            if (opt.emit) {
              this.selectedDocument.emit(this.selectedDoc);
            }
          }
        }
      ))))
      .subscribe();

    this.loadDocument(this.documentId, false);

    // Debounce/minLength widened from Product's 150ms/3 chars (2026-09-16, real
    // live evidence): unlike Product's FastSearch, WY_tvf_searchDocumentFast is
    // confirmed slow/unreliable under this route's first real traffic -- one
    // request took 13.4s and two adjacent identical ones hit the API's own
    // 30s mssql request timeout outright (see WORKLOG.md same date). This
    // doesn't fix the query -- that needs SQL-side work this session has no DB
    // access to do -- it only reduces how often a keystroke fires one.
    this.searchStr$.pipe(
      debounceTime(400),
      filter(s => (s ?? '').length > 3),
      switchMap(str => this.docApi.getDocumentFastSearch(str, this.filterClientId, this.filterLcoClientId, this.filterDocType).pipe(
        map(data => { this.docSearchResults = data; }),
        catchError(err => {
          console.error('*** DocumentselectorComponent getDocumentFastSearch error:', err);
          return EMPTY;
        })
      )),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  /** Emits the currently resolved `selectedDoc` without modifying it. */
  onDocumentSelectButton(event) {
    console.log('*** DocumentselectorComponent onDocumentSelectButton  ' + JSON.stringify(event));
//    this.selectedDoc = null;
    this.selectedDocument.emit(this.selectedDoc);

  }

  /** Clears the selection and emits `null` via `selectedDocument`. */
  onDocumentResetButton(event) {
    console.log('*** DocumentselectorComponent onDocumentResetButton  ' + JSON.stringify(event));
    this.selectedDoc = null;
    this.selectedDocStr = '';
    this.selectedDocId = null;
    this.selectedDocument.emit(this.selectedDoc);
  }

  /** Opens the edit view for the currently selected document in a new browser tab,
   *  so the selector's own form stays open behind it (2026-09-14, owner: same-tab
   *  navigation away from an in-progress form was uncomfortable). */
  onDocumentOpenButton(event) {
    console.log('*** DocumentselectorComponent onDocumentOpenButton  ' + JSON.stringify(event));
    // `Router.serializeUrl()` only turns a UrlTree into a path string -- it
    // knows nothing about the app's LocationStrategy, so it never adds the
    // `#` this app's HashLocationStrategy (`useHash: true`, app-routing.module.ts)
    // requires. `Location.prepareExternalUrl()` is the strategy-aware step
    // that does -- without it `window.open()` opens a plain path Apache has
    // no route for (404), confirmed live 2026-09-15 (`/edit-client/:id`,
    // same bug, `CompanyselectorComponent.onEditButton`).
    const path = this.router.serializeUrl(this.router.createUrlTree(['/edit-document', this.selectedDoc.DOCTYPE, this.selectedDoc.DOCID]));
    window.open(this.location.prepareExternalUrl(path), '_blank');
    this.messageService.add({ severity: 'info', summary: 'Открывается документ', detail: this.selectedDoc.DOCNAMESTR });
  }

  /** Opens the `app-documents-tree` dialog so the user can navigate the document hierarchy. */
  onDocumentTreeButton(event) {
    console.log('*** DocumentselectorComponent onDocumentTreeButton  ' + JSON.stringify(event));
    this.displayDC = true;
  }

  /** Receives a selected node from the documents-tree dialog and loads it with `emit=true`. */
  onSelectDocumentTreeNode(event: DocListEntry) {
    console.log('*** DocumentsComponent onSelectDocumentTreeNode: ' + JSON.stringify(event));

    this.loadDocument(event.DOCID, true);
  }

  /** Opens the `app-documents-search` dialog so the user can find a document by text. */
  onDocumentSearchButton(event) {
    console.log('*** DocumentselectorComponent onDocumentSearchButton  ' + JSON.stringify(event));
    this.displayDS = true;
  }

  /** Receives the selected document ID from the documents-search dialog and loads it with `emit=true`. */
  onDocumentSearch(event) {
    console.log('*** DocumentsComponent onDocumentSearch: ' + JSON.stringify(event));

    this.loadDocument(event, true);
  }

  /**
   * Forwards the `completeMethod` event query from `p-autoComplete` into the `searchStr$`
   * pipeline. Same sanitization as `ProductselectorComponent.doSearch()` — collapses
   * embedded newlines/tabs and caps the length before it reaches the API.
   */
  doSearch(event: { query: string }): void {
    const sanitized: string = (event.query ?? '').replace(/[\r\n\t]+/g, ' ').trim().slice(0, 255);
    this.searchStr$.next(sanitized);
  }

  /**
   * Fires on `p-autoComplete`'s `(onSelect)` when the user picks a live-search suggestion.
   * The suggestion is a reduced `DocumentFastSearchResult` row, not a full `Document` — and
   * PrimeNG's `[(ngModel)]="selectedDocStr"` two-way binding has already overwritten
   * `selectedDocStr` with that raw row object by the time this handler runs. Clear it back
   * to a string immediately (avoids a flash of `[object Object]`) and resolve the real
   * document the same way the tree/search-dialog paths do.
   */
  onDocumentAutoCompleteSelect(event: { originalEvent: Event, value: DocumentFastSearchResult }): void {
    console.log('*** DocumentselectorComponent onDocumentAutoCompleteSelect  ' + JSON.stringify(event.value));
    this.selectedDocStr = '';
    if (event.value?.DOCID) {
      this.loadDocument(event.value.DOCID, true);
    }
  }

  /**
   * Renders one `p-autoComplete` suggestion row for display: document type name,
   * number, partner name, and date, e.g.
   * `Договор-Заказ — №13029 — ООО Ромашка — 12.09.2026`.
   *
   * `DOCTYPENAME` (PR #80) is `null` if the row's `DOCTYPE` has no matching
   * `dbo.webDocTypes` row — omitted from the joined string rather than shown as
   * a literal "null". `DOCDATE` arrives as a string at runtime despite
   * `DocumentFastSearchResult`'s `Date` type annotation (HttpClient never
   * revives ISO date strings — see this repo's CLAUDE.md "Date fields —
   * HttpClient never revives them"), so it's guarded before calling any
   * `Date`-only method, per that same rule.
   */
  formatFastSearchResult(row: DocumentFastSearchResult): string {
    const dateStr = row.DOCDATE ? new Date(row.DOCDATE).toLocaleDateString() : '';
    const partner = row.PARTNERNIKNAME || row.PARTNERNAME || '';
    return [row.DOCTYPENAME, '№' + row.DOCNUMBER, partner, dateStr].filter(v => v).join(' — ');
  }
}



